import React, {
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, Redirect, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import {
  FaUsers,
  FaSearch,
  FaSyncAlt,
  FaUserPlus,
  FaChevronDown,
  FaMoneyBillWave,
  FaBriefcase,
  FaCalendarAlt,
  FaCoffee,
  FaHome,
  FaEdit,
  FaInfoCircle,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import { mdl, formatINR, formatDateDMY } from "../dialog/payrollMonthlyShared";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/admission-dashboard.css";

const MdlSelectSession = React.lazy(() => import("../dialog/MdlSelectSession"));
const MdlEditPayRollStaffDetails = React.lazy(() =>
  import("../dialog/MdlEditPayRollStaffDetails")
);

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const ui = {
  pageBg: "#f5f7fb",
  surface: "#ffffff",
  border: "rgba(148, 163, 184, 0.32)",
  divider: "rgba(148, 163, 184, 0.22)",
  muted: "#64748b",
  text: "#0f172a",
  textSoft: "#334155",
  radiusLg: 16,
  shadowSoft:
    "0 1px 2px rgba(15, 23, 42, 0.04), 0 2px 8px rgba(15, 23, 42, 0.04)",
};

//Created By Cursor
function staffInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

//Created By Cursor
const StaffAvatar = memo(function StaffAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="psl-avatar" aria-hidden>
      {showImg ? (
        <img src={imagePath} alt="" onError={() => setImgFailed(true)} />
      ) : (
        <span className="psl-avatar__initials">{staffInitials(name)}</span>
      )}
    </div>
  );
});

//Created By Cursor
const PayrollStaffCard = memo(function PayrollStaffCard({
  staff,
  index,
  busy,
  onEdit,
  onDetails,
}) {
  const salary = staff.basic_salary ?? staff.salary;
  const role = staff.staff_role || staff.designation || "";
  const hours = staff.working_hour ? `${staff.working_hour}h` : "";

  return (
    <article className="psl-card" style={{ "--rn-i": index }}>
      <div className="psl-card__body">
        <div className="psl-card__identity">
          <StaffAvatar
            name={staff.staff_name}
            imagePath={staff.staff_image_path}
          />
          <div className="psl-card__who min-w-0">
            <h3 className="psl-card__name">{staff.staff_name || "—"}</h3>
            <div className="psl-card__chips">
              {role ? (
                <span className="psl-chip psl-chip--role">{role}</span>
              ) : null}
              {staff.department_name ? (
                <span className="psl-chip psl-chip--dept">
                  {staff.department_name}
                </span>
              ) : null}
              {hours ? (
                <span className="psl-chip psl-chip--hours">{hours}/day</span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="psl-stats" role="group" aria-label="Payroll details">
          <div className="psl-stat psl-stat--salary">
            <span className="psl-stat__icon" aria-hidden>
              <FaMoneyBillWave />
            </span>
            <div className="psl-stat__text">
              <span className="psl-stat__label">Basic</span>
              <span className="psl-stat__value">{formatINR(salary)}</span>
            </div>
          </div>
          <div className="psl-stat psl-stat--type">
            <span className="psl-stat__icon" aria-hidden>
              <FaBriefcase />
            </span>
            <div className="psl-stat__text">
              <span className="psl-stat__label">Type</span>
              <span className="psl-stat__value">
                {staff.salary_type || "—"}
              </span>
            </div>
          </div>
          <div className="psl-stat psl-stat--joined">
            <span className="psl-stat__icon" aria-hidden>
              <FaCalendarAlt />
            </span>
            <div className="psl-stat__text">
              <span className="psl-stat__label">Joined</span>
              <span className="psl-stat__value">
                {formatDateDMY(staff.joining_date)}
              </span>
            </div>
          </div>
          <div className="psl-stat psl-stat--off">
            <span className="psl-stat__icon" aria-hidden>
              <FaCoffee />
            </span>
            <div className="psl-stat__text">
              <span className="psl-stat__label">Week off</span>
              <span className="psl-stat__value">{staff.week_off || "—"}</span>
            </div>
          </div>
        </div>

        <div className="psl-card__actions">
          <button
            type="button"
            className="psl-action-btn psl-action-btn--edit"
            onClick={() => onEdit(staff)}
            disabled={busy}
            title="Edit payroll details"
          >
            <FaEdit aria-hidden />
            <span>Edit</span>
          </button>
          <button
            type="button"
            className="psl-action-btn psl-action-btn--details"
            onClick={() => onDetails(staff)}
            disabled={busy}
            title="View details"
          >
            <FaInfoCircle aria-hidden />
            <span>Details</span>
          </button>
        </div>
      </div>
    </article>
  );
});

//Created By Cursor
function PayRollStaffList() {
  const history = useHistory();
  const location = useLocation();
  const loggedIn = localStorage.getItem("token") != null;

  const querySessionId = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("session_id") || "";
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const reqIdRef = useRef(0);

  //Created By Cursor
  useEffect(() => {
    window.scrollTo(0, 0);
    const mSession = SessionManager.getCurrentSession();
    const current = mSession?.[0] || null;

    if (querySessionId) {
      setSessionId(querySessionId);
      if (current && String(current.session_id) === String(querySessionId)) {
        setSessionName(current.session_name || "");
        setSessionStatus(current.status || "");
        return;
      }

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) return;
      axios
        .get(`${API_BASE()}getSessionList.php`, {
          params: { school_code: schoolCode },
        })
        .then((res) => res.data)
        .then((payload) => {
          if (payload?.api_status !== "OK" || !Array.isArray(payload.data)) return;
          const found = payload.data.find(
            (s) => String(s.session_id) === String(querySessionId)
          );
          if (found) {
            setSessionName(found.session_name || "");
            setSessionStatus(found.status || "");
          }
        })
        .catch((err) => {
          console.error("[PayRollStaffList] session resolve error:", err);
        });
      return;
    }

    if (current) {
      setSessionId(current.session_id || "");
      setSessionName(current.session_name || "");
      setSessionStatus(current.status || "");
    } else {
      setLoading(false);
    }
  }, [querySessionId]);

  //Created By Cursor
  const fetchStaffList = useCallback(
    async ({ soft = false } = {}) => {
      if (!sessionId) {
        setStaffList([]);
        setLoading(false);
        return;
      }
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        setLoading(false);
        return;
      }

      const reqId = ++reqIdRef.current;
      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const res = await axios.get(`${API_BASE()}getPayRollStaffList.php`, {
          params: {
            school_code: schoolCode,
            session_id: sessionId,
          },
        });
        if (reqId !== reqIdRef.current) return;
        if (res.data?.api_status === "OK" && Array.isArray(res.data.data)) {
          setStaffList(res.data.data);
        } else {
          setStaffList([]);
          if (soft) {
            myToast.notify_warning(
              res.data?.api_remark || "No payroll staff found."
            );
          }
        }
      } catch (error) {
        if (reqId !== reqIdRef.current) return;
        console.error("[PayRollStaffList] load error:", error);
        setStaffList([]);
        myToast.notify_warning("Failed to load payroll staff.");
      } finally {
        if (reqId === reqIdRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [sessionId]
  );

  useEffect(() => {
    fetchStaffList();
  }, [fetchStaffList]);

  const filteredList = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return staffList;
    return staffList.filter((s) => {
      const hay = [
        s.staff_id,
        s.staff_name,
        s.staff_role,
        s.designation,
        s.department_name,
        s.salary_type,
        s.week_off,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [staffList, searchTerm]);

  //Created By Cursor
  const handleSelectedSession = useCallback(
    (nextSessionId, nextSessionName, nextSessionStatus) => {
      setSessionId(nextSessionId || "");
      setSessionName(nextSessionName || "");
      setSessionStatus(nextSessionStatus || "");
      setSessionDialogOpen(false);
      setSearchTerm("");
      history.replace({
        pathname: "/AdminMain/PayRollStaffList",
        search: nextSessionId
          ? `?session_id=${encodeURIComponent(String(nextSessionId))}`
          : "",
      });
    },
    [history]
  );

  //Created By Cursor
  const handleEdit = useCallback((staff) => {
    setEditStaff(staff);
  }, []);

  //Created By Cursor
  const handleDetails = useCallback(
    (staff) => {
      const staffId = staff?.staff_id;
      if (!staffId) {
        myToast.notify_warning("Staff id is missing.");
        return;
      }
      history.push({
        pathname: `/AdminMain/PayRollStaffDetail/${encodeURIComponent(
          String(staffId)
        )}`,
        search: sessionId
          ? `?session_id=${encodeURIComponent(String(sessionId))}`
          : "",
      });
    },
    [history, sessionId]
  );

  //Created By Cursor
  const handleEditSaved = useCallback(async () => {
    setEditStaff(null);
    await fetchStaffList({ soft: true });
  }, [fetchStaffList]);

  if (!loggedIn) return <Redirect to="/Login" />;

  const busy = loading || refreshing;
  const sessionLabel = sessionName
    ? `${sessionName}${sessionStatus ? ` · ${sessionStatus}` : ""}`
    : sessionId
    ? "Selected session"
    : "Choose session";

  return (
    <div
      className="content payroll-page sm-page rn-page psl-page"
      style={{
        background: ui.pageBg,
        fontFamily: mdl.font,
        color: ui.text,
        minHeight: "100%",
      }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center psl-topbar"
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="psl-home-btn"
            aria-label="Back to payroll"
            onClick={() => history.goBack()}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span className="psl-topbar__crumb">Payroll / Staff List</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="psl-wrap">
        <div className="psl-shell">
          <header
            className="sm-hero"
            style={{ "--sm-hero-bg": HERO_GRADIENT, borderRadius: 0 }}
          >
            <div className="sm-hero__brand">
              <div className="sm-hero__icon" aria-hidden>
                <FaUsers />
              </div>
              <div className="min-w-0">
                <h1 className="sm-hero__title">Payroll Staff List</h1>
                <p className="sm-hero__sub">
                  {sessionLabel}
                  {staffList.length
                    ? ` · ${staffList.length} staff in salary system`
                    : " · Staff enrolled in salary system"}
                </p>
              </div>
            </div>

            <div className="sm-hero__tools">
              <button
                type="button"
                className="psl-session-pill"
                onClick={() => setSessionDialogOpen(true)}
                title="Change session"
              >
                <span
                  className={`psl-session-pill__dot${
                    sessionStatus && /current|active/i.test(sessionStatus)
                      ? " is-active"
                      : ""
                  }`}
                  aria-hidden
                />
                <span className="psl-session-pill__text text-truncate">
                  {sessionName || "Choose session…"}
                </span>
                {sessionStatus ? (
                  <span className="psl-session-pill__status">{sessionStatus}</span>
                ) : null}
                <FaChevronDown aria-hidden />
              </button>
              <div className="sm-hero__search-group">
                <FaSearch className="rn-search-icon" aria-hidden />
                <input
                  type="search"
                  className="sm-hero__search"
                  placeholder="Search staff…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Search payroll staff"
                  disabled={!sessionId}
                />
              </div>
              <button
                type="button"
                className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
                onClick={() => fetchStaffList({ soft: true })}
                disabled={busy || !sessionId}
                aria-label="Refresh list"
                title="Refresh"
              >
                <FaSyncAlt aria-hidden />
              </button>
              <Link
                to="/AdminMain/PayRollAddStaff"
                className="btn lift payroll-cta d-inline-flex align-items-center gap-1 psl-add-btn"
              >
                <FaUserPlus /> Add Staff
              </Link>
            </div>
          </header>

          <div className="rn-content psl-content">
            {!sessionId ? (
              <div className="sm-empty">
                <FaUsers className="rn-empty-icon" aria-hidden />
                <p className="sm-empty__title">Select a session</p>
                <p className="sm-empty__hint">
                  Choose an academic session to load payroll staff
                </p>
                <button
                  type="button"
                  className="btn lift payroll-cta psl-empty-cta"
                  onClick={() => setSessionDialogOpen(true)}
                >
                  Choose session
                </button>
              </div>
            ) : loading ? (
              <div className="rn-loading">
                <MyLoader />
              </div>
            ) : (
              <>
                {filteredList.length === 0 ? (
                  <div className="sm-empty">
                    <FaUsers className="rn-empty-icon" aria-hidden />
                    <p className="sm-empty__title">
                      {searchTerm.trim()
                        ? "No matching staff"
                        : "No payroll staff found"}
                    </p>
                    <p className="sm-empty__hint">
                      {searchTerm.trim()
                        ? "Try a different search term."
                        : "Add staff via Payroll → Add Staff for this session."}
                    </p>
                    {!searchTerm.trim() ? (
                      <Link
                        to="/AdminMain/PayRollAddStaff"
                        className="btn lift payroll-cta psl-empty-cta"
                      >
                        <FaUserPlus /> Add Staff
                      </Link>
                    ) : null}
                  </div>
                ) : (
                  <div className="psl-list">
                    {filteredList.map((staff, index) => (
                      <PayrollStaffCard
                        key={`${staff.staff_id}-${index}`}
                        staff={staff}
                        index={index}
                        busy={busy}
                        onEdit={handleEdit}
                        onDetails={handleDetails}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {sessionDialogOpen ? (
        <Suspense fallback={null}>
          <MdlSelectSession
            action={() => setSessionDialogOpen(false)}
            action2={handleSelectedSession}
          />
        </Suspense>
      ) : null}

      {editStaff ? (
        <Suspense fallback={null}>
          <MdlEditPayRollStaffDetails
            show={Boolean(editStaff)}
            actionClose={() => setEditStaff(null)}
            staffRecord={editStaff}
            sessionId={sessionId}
            onSaved={handleEditSaved}
          />
        </Suspense>
      ) : null}
    </div>
  );
}

export default PayRollStaffList;
